/* ============================================================
   SUSUNAN AGENT — satu takrifan untuk SEMUA senarai agent
   ============================================================
   Agent pelanggan (`users/{uid}/chatgptTemplates`, penanda agentV:2)
   dipapar di TIGA tempat: panel "My Agent" pada hub PRO Studio, senarai
   penuh di dalam halaman Agent, dan pemilih agent di Workspace.

   Dahulu ketiga-tiganya menyusun sendiri ikut abjad. Sebaik pelanggan
   boleh menyusun senarai sendiri, susunan itu WAJIB datang daripada satu
   tempat sahaja — kalau satu senarai ikut pilihan pelanggan dan satu lagi
   kekal ikut abjad, pelanggan membacanya sebagai sistem rosak.

   ⭐ Susunan disimpan sebagai medan `order` pada doc agent, BUKAN dalam
      localStorage: pelanggan guna 3-5 browser, dan susunan yang hanya
      hidup pada satu browser bermakna dia menyusun semula setiap kali
      bertukar browser.

   ⚠️ `order` sengaja TIADA dalam `toDoc` ProStudioAgentSection — biarkan
      begitu. Hanya laluan susun di bawah yang menulis medan itu. Kalau
      borang agent turut menulisnya, salinan tempatan yang sudah lama
      terbuka boleh memulangkan susunan lama selepas pelanggan menyusun
      di browser lain.
   ============================================================ */

import { deleteDoc, doc, setDoc, writeBatch } from 'firebase/firestore';

import { db } from '../firebase';

const AGENT_COL = 'chatgptTemplates';

/* Agent yang belum pernah disusun jatuh ke HUJUNG, bukan ke pangkal.
   Sebab: sebelum susunan pertama dibuat TIADA satu pun agent membawa
   `order`, jadi semuanya seri dan senarai kekal ikut abjad — sama seperti
   sebelum ciri ini ada. Tiada apa bergerak dengan sendiri. */
const TIADA_SUSUNAN = Number.MAX_SAFE_INTEGER;

export function nilaiSusunan(d) {
    const n = Number(d?.order);
    return Number.isFinite(n) ? n : TIADA_SUSUNAN;
}

/* Pembanding untuk doc MENTAH Firestore, bukan bentuk UI. Ketiga-tiga
   senarai memetakan doc kepada bentuk yang berbeza-beza, jadi menyusun
   SEBELUM pemetaan ialah satu-satunya cara satu pembanding boleh
   berkhidmat kepada ketiga-tiganya. */
export function bandingAgent(a, b) {
    const sa = nilaiSusunan(a);
    const sb = nilaiSusunan(b);
    if (sa !== sb) return sa - sb;
    return String(a?.name || '').localeCompare(String(b?.name || ''));
}

/* Susunan untuk agent yang BARU dicipta. Pulang null selagi tiada
   seorang pun agent membawa susunan — memberi 0 kepada agent baharu
   ketika semua yang lain "tiada susunan" akan melantik agent baharu itu
   ke ATAS senarai, bukan ke bawah tempat pelanggan menjangkanya. */
export function susunanBaru(senarai = []) {
    const ada = senarai.map(nilaiSusunan).filter((n) => n !== TIADA_SUSUNAN);
    return ada.length ? Math.max(...ada) + 1 : null;
}

/* Alih item ke kedudukan AKHIR yang diberi — dipakai oleh anak panah
   naik/turun (`ke` = indeks semasa ± 1). Tulen, tiada kesan tepi. */
export function alihItem(senarai, dari, ke) {
    if (!Array.isArray(senarai) || dari === ke) return senarai;
    if (dari < 0 || dari >= senarai.length || ke < 0 || ke >= senarai.length) return senarai;
    const keluar = [...senarai];
    const [item] = keluar.splice(dari, 1);
    keluar.splice(ke, 0, item);
    return keluar;
}

/* Alih item ke TITIK SISIPAN yang diberi — dipakai oleh tarik-lepas,
   di mana sasarannya ialah celah antara dua baris (0 = atas sekali,
   panjang senarai = bawah sekali).

   ⚠️ Tolakan `sisip > dari ? sisip - 1 : sisip` itu WAJIB: item dibuang
      daripada senarai dahulu, jadi setiap celah di BAWAHnya bergerak
      naik satu tempat. Tanpa tolakan itu, menarik satu baris ke bawah
      sebanyak satu tempat tidak menghasilkan apa-apa perubahan. */
export function sisipItem(senarai, dari, sisip) {
    if (!Array.isArray(senarai)) return senarai;
    if (dari < 0 || dari >= senarai.length) return senarai;
    if (sisip === dari || sisip === dari + 1) return senarai;
    const keluar = [...senarai];
    const [item] = keluar.splice(dari, 1);
    keluar.splice(sisip > dari ? sisip - 1 : sisip, 0, item);
    return keluar;
}

/* Tarik-lepas HTML5 tidak berfungsi pada skrin sentuh — tiada acara
   dragstart daripada satu sentuhan. Jadi setiap jenis peranti dapat SATU
   kaedah susun, bukan kedua-dua (arahan user 2026-08-19):

     peranti bertetikus  →  pemegang + tarik-lepas SAHAJA
     peranti sentuh      →  anak panah naik/turun SAHAJA

   ⛔ Jangan render pemegang itu pada telefon "supaya nampak sama" — ia
      menjadi tepat jenis kawalan yang nampak boleh ditekan tetapi tidak
      membuat apa-apa. Dan jangan pulangkan anak panah kepada komputer:
      tarik-lepas sudah cukup di sana, dan dua butang tambahan pada setiap
      baris cuma menyesakkan baris.                                    */
export function bolehTarikTetikus() {
    try {
        return !!window.matchMedia?.('(hover: hover) and (pointer: fine)')?.matches;
    } catch {
        return true;
    }
}

/* Tulis susunan penuh dalam SATU batch. Semua agent dalam senarai ditulis,
   bukan hanya yang bergerak — itu yang menghapuskan keadaan bercampur
   "sebahagian ada susunan, sebahagian tidak" pada susunan pertama.
   8 agent = 8 tulisan; kos tidak terasa. */
export async function simpanSusunanAgent(uid, ids) {
    if (!uid || !Array.isArray(ids) || ids.length === 0) return;
    const batch = writeBatch(db);
    ids.forEach((id, i) => {
        batch.set(doc(db, 'users', uid, AGENT_COL, id), { order: i }, { merge: true });
    });
    await batch.commit();
}

/* Padam satu agent.

   Ia duduk di sini bersama pembanding dan penulis susunan kerana dalam rupa
   v3.9 senarai agent pada hub PRO Studio ialah SATU-SATUNYA senarai yang
   tinggal — skrin senarai di dalam halaman Agent dibuang 2026-08-21 atas
   arahan user — jadi tong sampah pada baris hub itulah satu-satunya laluan
   padam yang ada. Pengesahan ialah kerja pemanggil: ia yang tahu nama agent
   untuk dipaparkan kepada pelanggan.

   ⚠️ ProStudioAgentSection MASIH memegang salinan padamnya sendiri untuk rupa
      LAMA (senarai di sana kekal — lihat nota dalam fail itu). Kedua-duanya
      hanya memadam satu doc: tiada subdoc, tiada fail Storage, jadi tiada
      peraturan yang boleh terpesong antara dua salinan. Kalau agent suatu hari
      membawa data sampingan, satukan kedua-duanya DI SINI dahulu. */
export async function padamAgent(uid, id) {
    if (!uid || !id) return;
    await deleteDoc(doc(db, 'users', uid, AGENT_COL, id));
}

/* ── Papar di Workspace (toggle pada baris agent, arahan user 2026-10-18) ──
   Medan `showInWorkspace` pada doc agent. TIADA medan = PAPAR — semua agent
   yang wujud sebelum ciri ini kekal muncul di Workspace tanpa migrasi, dan
   agent baharu (cipta di ProStudioAgentSection) pun mula dengan ON.

   ⭐ Agent BELUM Configure = AUTO OFF (arahan user 2026-10-18), tanpa
      mengira medan — ia tidak boleh di-Run, jadi tiada guna dalam pemilih.
      Sebaik Configure siap ia muncul sendiri (kecuali pelanggan OFF-kan).
      Tiada tulisan data untuk ini: dikira daripada `configured`.

   ⚠️ KEMBAR peraturan "tiada = papar": `agentDiWorkspace` di bawah dipakai
      oleh hub (useSenaraiAgent) DAN pemilih agent WorkspaceView. Jangan tulis
      semula semakan `!== false` di tempat lain — import fungsi ini.
   ⚠️ Seperti `order`, medan ini sengaja TIADA dalam `toDoc`
      ProStudioAgentSection; simpanan Configure guna `merge: true` jadi ia
      tidak tertindih. Hanya toggle ini yang menulisnya. */
export function agentDiWorkspace(d) {
    return !!d?.configured && d?.showInWorkspace !== false;
}

export async function tetapAgentDiWorkspace(uid, id, papar) {
    if (!uid || !id) throw new Error('tiada sesi log masuk');
    await setDoc(doc(db, 'users', uid, AGENT_COL, id), { showInWorkspace: !!papar }, { merge: true });
}
